import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import ChangePasswordModal from '../Common Modals/changepassword';
import StudentInfoModal from './Modals/info';
import ClassDetailsModal from './Modals/classdetails';
import TimetableModal from './Modals/timetable';
import AttendanceModal from './Modals/AttendanceModal';
import CIEMarksModal from './Modals/CIEMarksModal';

const StudentDashboard = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  
  const { userId, tenantId, mustChangePassword } = state || {};
  
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTimetableModal, setShowTimetableModal] = useState(false);
  const [classStats, setClassStats] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);

  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showStudentInfoModal, setShowStudentInfoModal] = useState(false);
  const [showClassDetailsModal, setShowClassDetailsModal] = useState(false);
  const [showCIEMarksModal, setShowCIEMarksModal] = useState(false);

  // Use refs to prevent unnecessary re-renders
  const hasFetchedData = useRef(false);
  const lastFetchTime = useRef(0);
  const FETCH_COOLDOWN_MS = 30000;
  const hasLoggedProps = useRef(false); // Track if props have been logged

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check if user must change password on component mount
  useEffect(() => {
    if (mustChangePassword) {
      setShowPasswordModal(true);
    }
  }, [mustChangePassword]);

  // Function to resolve profile photo URL
  const resolveProfilePhotoUrl = (profilePhotoUrl) => {
    if (!profilePhotoUrl) return null;
    if (profilePhotoUrl.startsWith('http')) {
      return profilePhotoUrl;
    }
    if (profilePhotoUrl.startsWith('/api/')) {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
      return `${cleanBaseUrl}${profilePhotoUrl}`;
    }
    return profilePhotoUrl;
  };

  // Calculate class statistics based on timetable
  const calculateClassStats = useCallback((timetableData) => {
    if (!timetableData?.timetable) return null;

    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentHours = now.getHours().toString().padStart(2, '0');
    const currentMinutes = now.getMinutes().toString().padStart(2, '0');
    const currentTimeString = `${currentHours}:${currentMinutes}`;

    let ongoingClass = null;
    let nextClass = null;
    let completedToday = 0;
    let remainingToday = 0;
    const upcomingClasses = [];

    const todayClasses = timetableData.timetable[currentDay] || [];
    
    todayClasses.forEach(period => {
      const periodStart = period.timeSlot.start;
      const periodEnd = period.timeSlot.end;
      
      if (currentTimeString >= periodStart && currentTimeString < periodEnd) {
        ongoingClass = {
          ...period,
          timeRemaining: calculateTimeRemaining(periodEnd, now)
        };
      }
      else if (currentTimeString < periodStart) {
        if (!nextClass || periodStart < nextClass.timeSlot.start) {
          nextClass = {
            ...period,
            timeUntil: calculateTimeUntil(periodStart, now)
          };
        }
        upcomingClasses.push({
          ...period,
          timeUntil: calculateTimeUntil(periodStart, now)
        });
        remainingToday++;
      }
      else if (currentTimeString >= periodEnd) {
        completedToday++;
      }
    });

    if (!nextClass && upcomingClasses.length === 0) {
      const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const currentDayIndex = dayOrder.indexOf(currentDay);
      
      for (let i = 1; i <= 7; i++) {
        const nextDayIndex = (currentDayIndex + i) % 7;
        const nextDay = dayOrder[nextDayIndex];
        const nextDayClasses = timetableData.timetable[nextDay] || [];
        
        if (nextDayClasses.length > 0) {
          nextClass = {
            ...nextDayClasses[0],
            day: nextDay,
            timeUntil: `Next ${nextDay}`
          };
          break;
        }
      }
    }

    return {
      ongoingClass,
      nextClass,
      upcomingClasses: upcomingClasses.slice(0, 3),
      completedToday,
      remainingToday,
      totalToday: todayClasses.length
    };
  }, []);

  // Helper functions
  const calculateTimeRemaining = (endTime, currentTime) => {
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    const endDate = new Date(currentTime);
    endDate.setHours(endHours, endMinutes, 0, 0);
    
    const diffMs = endDate - currentTime;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const calculateTimeUntil = (startTime, currentTime) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const startDate = new Date(currentTime);
    startDate.setHours(startHours, startMinutes, 0, 0);
    
    const diffMs = startDate - currentTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 0) return 'Now';
    if (diffMins < 60) return `in ${diffMins}m`;
    
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    return `in ${hours}h ${minutes}m`;
  };

  // Fetch student profile and class statistics
  const fetchStudentProfileAndStats = useCallback(async () => {
    if (hasFetchedData.current) {
      console.log('⏸️ Skipping fetch - data already loaded');
      return;
    }

    const now = Date.now();
    if (now - lastFetchTime.current < FETCH_COOLDOWN_MS) {
      console.log('⏸️ Skipping fetch - in cooldown period');
      return;
    }

    if (!userId) {
      setError('User ID not found. Please login again.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      lastFetchTime.current = now;
      
      const subdomain = window.location.hostname.split('.')[0];
      
      console.log('🔄 Fetching student profile and timetable...');
      
      const profileResponse = await api.post('/api/student/profile', {
        userId,
        subdomain
      });

      setStudentData(profileResponse.data.data);
      console.log('✅ Student profile loaded');

      let timetableData = null;
      try {
        const timetableResponse = await api.post('/api/student/timetable', {
          userId,
          subdomain,
          viewType: 'master'
        });
        timetableData = timetableResponse.data.data;
      } catch (ttErr) {
        if (ttErr.response && ttErr.response.status === 404) {
          timetableData = null;
        } else {
          throw ttErr;
        }
      }

      if (!timetableData || !timetableData.timetable || Object.keys(timetableData.timetable).length === 0) {
        setClassStats(null);
      } else {
        const stats = calculateClassStats(timetableData);
        setClassStats(stats);
        console.log('✅ Class statistics calculated');
      }

      hasFetchedData.current = true;

    } catch (err) {
      console.error('❌ Error fetching student data:', err);
      setError(err.response?.data?.message || 'Failed to load student data');
      hasFetchedData.current = false;
    } finally {
      setLoading(false);
    }
  }, [userId, calculateClassStats]);

  // Update only the timer display every second
  useEffect(() => {
    if (!classStats) return;
    let needsUpdate = false;
    const updatedStats = { ...classStats };
    if (classStats.ongoingClass) {
      const newTimeRemaining = calculateTimeRemaining(classStats.ongoingClass.timeSlot.end, currentTime);
      if (newTimeRemaining !== classStats.ongoingClass.timeRemaining) {
        updatedStats.ongoingClass = { ...classStats.ongoingClass, timeRemaining: newTimeRemaining };
        needsUpdate = true;
      }
    }
    if (classStats.nextClass) {
      const newTimeUntil = calculateTimeUntil(classStats.nextClass.timeSlot.start, currentTime);
      if (newTimeUntil !== classStats.nextClass.timeUntil) {
        updatedStats.nextClass = { ...classStats.nextClass, timeUntil: newTimeUntil };
        needsUpdate = true;
      }
    }
    if (needsUpdate) {
      setClassStats(updatedStats);
    }
  }, [currentTime, classStats]);

  useEffect(() => {
    fetchStudentProfileAndStats();
  }, [fetchStudentProfileAndStats]);

  const handlePasswordChangeSuccess = () => {
    console.log('✅ Password changed successfully');
    setShowPasswordModal(false);
  };

  const handleProfileClick = () => {
    setShowStudentInfoModal(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const getSemesterDisplay = () => {
    if (!studentData) return null;
    if (studentData.semester && studentData.semester.name) {
      return studentData.semester.name;
    }
    if (studentData.student?.semester) {
      return studentData.student.semester;
    }
    if (studentData.student?.semesterId) {
      const semesterNumber = parseInt(studentData.student.semesterId);
      if (!isNaN(semesterNumber)) {
        const suffix = ["th", "st", "nd", "rd"];
        const v = semesterNumber % 100;
        return `Semester ${semesterNumber}${suffix[(v - 20) % 10] || suffix[v] || suffix[0]}`;
      }
      return `Semester ${studentData.student.semesterId}`;
    }
    return null;
  };

  // Get CIE Marks Modal props with validation - Only log once
  const getCIEMarksModalProps = useCallback(() => {
    const programTypeId = studentData?.class?.programTypeId || 
                          studentData?.student?.programTypeId || 
                          studentData?.programTypeId;
    const programId = studentData?.class?.programId || 
                     studentData?.student?.programId || 
                     studentData?.programId;
    const academicYearId = studentData?.class?.academicYearId || 
                          studentData?.student?.academicYearId;
    const termId = studentData?.class?.termId || 
                  studentData?.student?.termId;
    const semesterId = studentData?.class?.semesterId || 
                      studentData?.student?.semesterId;

    const props = {
      userId,
      tenantId,
      academicYearId,
      programTypeId,
      programId,
      termId,
      semesterId,
      isValid: !!(userId && academicYearId && programTypeId && programId)
    };

    // Only log once when studentData is loaded
    if (studentData && !hasLoggedProps.current) {
      console.log('📊 CIEMarksModal Props (once):', {
        userId: props.userId,
        academicYearId: props.academicYearId,
        programTypeId: props.programTypeId,
        programId: props.programId,
        termId: props.termId,
        semesterId: props.semesterId,
        isValid: props.isValid
      });
      hasLoggedProps.current = true;
    }

    return props;
  }, [studentData, userId, tenantId]);

  // Reset log flag when studentData changes
  useEffect(() => {
    if (studentData) {
      hasLoggedProps.current = false;
    }
  }, [studentData]);

  // Quick links with CIE Marks - Defined once, not on every render
  const quickLinks = React.useMemo(() => [
    { name: 'Attendance', icon: '📅', color: 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100', action: () => setShowAttendanceModal(true) },
    { name: 'Subjects', icon: '📚', color: 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-100', action: () => setShowClassDetailsModal(true) },
    { 
      name: 'CIE Marks', 
      icon: '📊', 
      color: 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100', 
      action: () => {
        const props = getCIEMarksModalProps();
        if (!props.isValid) {
          console.warn('⚠️ Cannot open CIE Marks modal - missing required props');
          alert('Cannot load CIE Marks. Required data is missing. Please contact support.');
          return;
        }
        console.log('✅ Opening CIE Marks modal');
        setShowCIEMarksModal(true);
      }
    },
    { name: 'Timetable', icon: '⏰', color: 'bg-purple-50 text-purple-600 border-purple-100 hover:bg-purple-100', action: () => setShowTimetableModal(true) },
    { name: 'Results', icon: '🏆', color: 'bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100', action: () => console.log('Results clicked') },
  ], [getCIEMarksModalProps]);

  // Get CIE Modal Props for rendering - only when needed
  const cieModalProps = studentData ? getCIEMarksModalProps() : null;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-600 border-t-transparent mx-auto mb-3"></div>
          <p className="text-sm font-medium text-slate-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 max-w-sm w-full border border-slate-200 text-center shadow-sm">
          <div className="text-rose-500 text-3xl mb-3">⚠️</div>
          <p className="text-sm font-semibold text-slate-800 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Get initials for avatar
  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'S';
  };

  // Profile Avatar Component
  const ProfileAvatar = ({ size = 'md', clickable = true, showStatus = true }) => {
    const sizeClasses = {
      sm: 'h-9 w-9',
      md: 'h-12 w-12',
      lg: 'h-16 w-16',
      xl: 'h-20 w-20 sm:h-24 sm:w-24'
    };

    const textSizes = {
      sm: 'text-xs',
      md: 'text-base',
      lg: 'text-xl',
      xl: 'text-2xl sm:text-3xl'
    };

    const profilePhotoUrl = studentData?.student?.profilePhotoUrl 
      ? resolveProfilePhotoUrl(studentData.student.profilePhotoUrl)
      : null;

    const content = (
      <div className={`relative ${sizeClasses[size]} rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center overflow-hidden shrink-0 ${clickable ? 'cursor-pointer hover:bg-indigo-100 transition-colors' : ''}`}>
        {profilePhotoUrl ? (
          <img
            src={profilePhotoUrl}
            alt={`${studentData?.student?.firstName || 'Student'} ${studentData?.student?.lastName || ''}`}
            className="h-full w-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <span 
          className={`text-indigo-600 font-bold ${textSizes[size]} ${
            profilePhotoUrl ? 'hidden' : 'flex'
          } items-center justify-center h-full w-full`}
        >
          {getInitials((studentData?.student?.firstName || '') + ' ' + (studentData?.student?.lastName || ''))}
        </span>
        
        {showStatus && (
          <div className="absolute bottom-0 right-0 p-0.5 bg-white rounded-full">
            <div className={`w-2.5 h-2.5 rounded-full border border-white ${
              studentData?.student?.status === 'active' ? 'bg-emerald-500' : 'bg-rose-500'
            }`}></div>
          </div>
        )}
      </div>
    );

    if (clickable) {
      return <button onClick={handleProfileClick} className="focus:outline-none rounded-full">{content}</button>;
    }
    return content;
  };

  const semesterDisplay = getSemesterDisplay();

  if (typeof window !== 'undefined') {
    window.__studentDataForAttendanceModal = studentData;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans antialiased">
      {/* Attendance Modal */}
      <AttendanceModal
        isOpen={showAttendanceModal}
        onClose={() => setShowAttendanceModal(false)}
        userId={userId}
        tenantId={tenantId}
      />

      {/* Password Change Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userId={userId}
        userRole="student"
        isForced={mustChangePassword}
        onSuccess={handlePasswordChangeSuccess}
      />

      {/* Student Info Modal */}
      <StudentInfoModal
        isOpen={showStudentInfoModal}
        onClose={() => setShowStudentInfoModal(false)}
        userId={userId}
        title="My Profile"
      />

      {/* Class Details Modal */}
      <ClassDetailsModal
        isOpen={showClassDetailsModal}
        onClose={() => setShowClassDetailsModal(false)}
        userId={userId}
        title="Class Details & Subjects"
      />

      {/* Timetable Modal */}
      <TimetableModal
        isOpen={showTimetableModal}
        onClose={() => setShowTimetableModal(false)}
        userId={userId}
        title="My Timetable"
      />

      {/* CIE Marks Modal - Only render if props are valid */}
      {cieModalProps && cieModalProps.isValid && (
        <CIEMarksModal
          isOpen={showCIEMarksModal}
          onClose={() => {
            console.log('🔚 Closing CIE Marks modal');
            setShowCIEMarksModal(false);
          }}
          userId={cieModalProps.userId}
          tenantId={cieModalProps.tenantId}
          academicYearId={cieModalProps.academicYearId}
          programTypeId={cieModalProps.programTypeId}
          programId={cieModalProps.programId}
          termId={cieModalProps.termId}
          semesterId={cieModalProps.semesterId}
        />
      )}

      {/* Main Content Wrapper */}
      <div className={`transition-all duration-300 ${(showPasswordModal || showStudentInfoModal || showClassDetailsModal || showTimetableModal || showCIEMarksModal) ? 'blur-md pointer-events-none select-none' : ''}`}>
        
        {/* Navigation Bar */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 gap-4">
              
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="h-9 w-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-sm">
                  S
                </div>
                <div className="flex flex-col overflow-hidden">
                  <h1 className="text-base font-bold text-slate-900 leading-tight">Student Portal</h1>
                  <span className="text-xs text-slate-500 truncate">
                    {studentData?.tenant?.name}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4 ml-auto shrink-0">
                {/* Desktop Time Display */}
                <div className="text-right hidden sm:block border-r border-slate-200 pr-4">
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Current Time</div>
                  <div className="font-mono text-sm font-semibold text-indigo-600">
                    {currentTime.toLocaleTimeString('en-US', { 
                      hour12: true, 
                      hour: '2-digit', 
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </div>
                </div>

                {/* Profile Interactive Badge */}
                <button
                  onClick={handleProfileClick}
                  className="flex items-center gap-2 hover:bg-slate-50 rounded-xl p-1 sm:pr-3 transition-colors group focus:outline-none"
                >
                  <ProfileAvatar size="sm" clickable={false} showStatus={false} />
                  <div className="text-left hidden md:block max-w-[120px]">
                    <div className="text-xs font-semibold text-slate-700 truncate group-hover:text-indigo-600 transition-colors">
                      {(studentData?.student?.firstName && studentData?.student?.lastName) 
                        ? `${studentData.student.firstName} ${studentData.student.lastName}`
                        : studentData?.student?.name || 'Student'}
                    </div>
                    <div className="text-[10px] text-slate-400">View Profile</div>
                  </div>
                </button>

                <button
                  onClick={handleLogout}
                  className="text-slate-400 hover:text-rose-600 p-2 sm:px-3 sm:py-1.5 hover:bg-rose-50 rounded-xl text-xs sm:text-sm font-semibold transition-all"
                >
                  <span className="hidden sm:inline">Logout</span>
                  <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 01-3-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>

            </div>
          </div>
        </header>

        {/* Core Layout Grid */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8 space-y-5 sm:space-y-6">
          
          {/* Welcome Banner Section */}
          <div className="relative bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-900 rounded-2xl p-5 sm:p-6 text-white shadow-sm overflow-hidden border border-slate-800">
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div className="flex-1 space-y-4">
                <div>
                  <h2 className="text-xl sm:text-3xl font-bold tracking-tight text-white">
                    Welcome back, {studentData?.student?.firstName || 'Student'}! 
                  </h2>
                  <p className="text-indigo-200/80 text-xs sm:text-sm mt-1">
                    Ready to explore your academic journey?
                  </p>
                </div>
                
                {/* Information Grid Parameters */}
                <div className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3 text-xs">
                    <div>
                      <span className="text-indigo-300 block font-medium">Class</span>
                      <span className="font-semibold text-sm block truncate mt-0.5 text-white">
                        {studentData?.class?.className || studentData?.student?.className || '--'}
                      </span>
                    </div>
                    <div>
                      <span className="text-indigo-300 block font-medium">Program Type</span>
                      <span className="font-semibold text-sm block truncate mt-0.5 text-white">
                        {studentData?.class?.programType?.code || '--'}
                      </span>
                    </div>
                    <div className="col-span-2 lg:col-span-1">
                      <span className="text-indigo-300 block font-medium">Program Name</span>
                      <span className="font-semibold text-sm block truncate mt-0.5 text-white">
                        {studentData?.class?.program?.name || '--'}
                      </span>
                    </div>
                    {studentData?.student?.section && (
                      <div>
                        <span className="text-indigo-300 block font-medium">Section</span>
                        <span className="font-semibold text-sm block truncate mt-0.5 text-white">
                          {studentData?.student?.section}
                        </span>
                      </div>
                    )}
                    {semesterDisplay && studentData?.class?.isSemesterBased !== false && (
                      <div>
                        <span className="text-indigo-300 block font-medium">Semester</span>
                        <span className="font-semibold text-sm block truncate mt-0.5 text-white">
                          {semesterDisplay}
                        </span>
                      </div>
                    )}
                    {studentData?.class?.isSemesterBased === false && studentData?.class?.termName && (
                      <div>
                        <span className="text-indigo-300 block font-medium">Term</span>
                        <span className="font-semibold text-sm block truncate mt-0.5 text-white">
                          {studentData.class.termName}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-indigo-300 block font-medium">USN</span>
                      <span className="font-mono font-semibold text-sm block truncate mt-0.5 text-white">
                        {studentData?.student?.USN || '--'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="hidden md:block shrink-0">
                <ProfileAvatar size="xl" showStatus={true} clickable={false} />
              </div>
            </div>
          </div>

          {/* Real-time Dynamic Class Scheduler Panels */}
          {classStats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              
              {/* Live Lecture Component */}
              {classStats.ongoingClass && (
                <div className="bg-white rounded-2xl shadow-sm border-2 border-emerald-500/30 p-5 relative overflow-hidden flex flex-col justify-between gap-3">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                      Live Class
                    </span>
                    <span className="text-xs font-mono font-bold bg-slate-900 text-white px-2 py-0.5 rounded">
                      {classStats.ongoingClass.timeRemaining} left
                    </span>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-slate-900 leading-tight">
                      {classStats.ongoingClass.displaySubject}
                    </h4>
                    {classStats.ongoingClass.faculty?.name && (
                      <p className="text-xs text-slate-500 mt-1">👨‍🏫 {classStats.ongoingClass.faculty.name}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-400 border-t border-slate-100 pt-3">
                    <span>🏢 {classStats.ongoingClass.room || 'Classroom'}</span>
                    <span className="font-medium text-slate-600 uppercase tracking-wider text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{classStats.ongoingClass.displayType}</span>
                  </div>
                </div>
              )}

              {/* Next Class Up Component */}
              {classStats.nextClass && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col justify-between gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-100">
                      Next Class
                    </span>
                    <span className="text-xs font-medium text-slate-500">
                      ⏰ {classStats.nextClass.timeUntil}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-bold text-base sm:text-lg text-slate-800 leading-tight truncate">
                      {classStats.nextClass.displaySubject}
                    </h4>
                    {classStats.nextClass.faculty?.name && (
                      <p className="text-xs text-slate-500 mt-1">👨‍🏫 {classStats.nextClass.faculty.name}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-400 border-t border-slate-100 pt-3">
                    <span>🏢 {classStats.nextClass.room || 'Campus Room'}</span>
                    <span className="font-medium text-slate-500">{classStats.nextClass.displayType}</span>
                  </div>
                </div>
              )}

              {/* Today's Stats Cards Block */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col justify-center gap-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Today's Summary</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl text-center">
                    <div className="text-xl font-bold text-slate-800">{classStats.completedToday}</div>
                    <div className="text-[11px] font-medium text-slate-400 uppercase mt-0.5">Completed</div>
                  </div>
                  <div className="p-3 bg-indigo-50/50 border border-indigo-100/30 rounded-xl text-center">
                    <div className="text-xl font-bold text-indigo-600">{classStats.remainingToday}</div>
                    <div className="text-[11px] font-medium text-indigo-400 uppercase mt-0.5">Remaining</div>
                  </div>
                  <div className="p-2 bg-slate-900 rounded-xl text-center col-span-2 flex items-center justify-between px-4">
                    <span className="text-xs text-slate-400 font-medium">Total Classes Today</span>
                    <span className="text-xs font-bold text-white bg-white/10 px-2 py-0.5 rounded">{classStats.totalToday} Slots</span>
                  </div>
                </div>
              </div>

            </div>
          ) : null}

          {/* Quick Actions Panel Matrix */}
          <div className="space-y-3">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900">Quick Actions</h3>
              <p className="text-xs text-slate-400">Access your academic resources</p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {quickLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={link.action}
                  className={`border rounded-xl p-4 flex flex-col items-center justify-center text-center transition-all hover:-translate-y-0.5 hover:shadow-sm group focus:outline-none ${link.color}`}
                >
                  <div className="text-2xl mb-2 group-hover:scale-105 transition-transform">
                    {link.icon}
                  </div>
                  <div className="text-xs font-bold leading-tight">
                    {link.name}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Informational Ledger Logs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            
            {/* Recent Activities Panel Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                <span className="text-blue-500">📈</span>
                Recent Activity
              </h3>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs gap-4">
                  <div className="flex items-center overflow-hidden gap-2">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full shrink-0" />
                    <span className="text-slate-600 truncate">Assignment submitted</span>
                  </div>
                  <span className="text-slate-400 font-medium whitespace-nowrap">2 hours ago</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs gap-4">
                  <div className="flex items-center overflow-hidden gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full shrink-0" />
                    <span className="text-slate-600 truncate">Attendance marked</span>
                  </div>
                  <span className="text-slate-400 font-medium whitespace-nowrap">Today</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl text-xs gap-4">
                  <div className="flex items-center overflow-hidden gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-400 rounded-full shrink-0" />
                    <span className="text-slate-600 truncate">Exam result published</span>
                  </div>
                  <span className="text-slate-400 font-medium whitespace-nowrap">Yesterday</span>
                </div>
              </div>
            </div>

            {/* Notification Board Card Block */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                <span className="text-orange-500">📢</span>
                Announcements
              </h3>
              <div className="space-y-3">
                <div className="p-3 bg-amber-50/40 border border-amber-100 rounded-xl space-y-1">
                  <h4 className="font-bold text-amber-900 text-xs sm:text-sm">Exam Schedule Released</h4>
                  <p className="text-amber-800 text-xs leading-relaxed">
                    Final examination schedule has been published. Check your timetable.
                  </p>
                  <div className="text-[10px] text-amber-500/80 font-medium pt-0.5">3 days ago</div>
                </div>
                <div className="p-3 bg-blue-50/40 border border-blue-100 rounded-xl space-y-1">
                  <h4 className="font-bold text-blue-900 text-xs sm:text-sm">Library Hours Extended</h4>
                  <p className="text-blue-700 text-xs leading-relaxed">
                    Library will remain open until 10 PM during exam period.
                  </p>
                  <div className="text-[10px] text-blue-500/80 font-medium pt-0.5">1 week ago</div>
                </div>
              </div>
            </div>

          </div>

        </main>
      </div>
    </div>
  );
};

export default StudentDashboard;